if (typeof DateTimeSlotPicker !== 'function') {

	class DateTimeSlotPicker extends HTMLElement {

		constructor() {
			super();
			this.init();
		}

		init() {
			this.dateInput = this.querySelector('input[type="date"]');
			this.timeSelect = this.querySelector('select[id$="-time"]');
			this.isPlatter = this.dataset.isPlatter === 'true';
			this.calendarOpen = false;

			// Lead days configuration
			this.leadDays = this.dataset.leadDays ? parseInt(this.dataset.leadDays) : (this.isPlatter ? 3 : 0);
			this.availableDays = this.dataset.availableDays ? parseInt(this.dataset.availableDays) : (this.isPlatter ? 10 : 2);
			this.maxDate = this.dataset.maxDate || null;

			// Cutoff time: 6:30 PM (18:30) - GMT+8 (Singapore/Perth)
			this.cutoffHour = 18;
			this.cutoffMinute = 30;

			// Parse timeslot configuration
			this.timeslotConfig = this.parseTimeslotConfig();
			this.startHour = this.timeslotConfig.startHour ?? 10;
			this.endHour = this.timeslotConfig.endHour ?? 20;
			this.slotDuration = this.timeslotConfig.slotDuration ?? 30;
			this.closedDates = this.timeslotConfig.closedDates || [];
			this.dateOverrides = this.timeslotConfig.dateOverrides || {};
			this.bufferMinutes = 45;

			// ✅ FIXED: GMT+8 = +480 minutes offset (NOT -480)
			this.timezoneOffset = 480;
			
			this.setupDateConstraints();
			this.populateTimeOptions();
			this.setupDateDisplay();
			this.setupCalendarPicker();
			
			requestAnimationFrame(() => {
				this.ensureArrowIcons();
			});
			
			if (this.dateInput) {
				// Single change handler - removed duplicate
				this.dateInput.addEventListener('change', () => {
					this.enforceDateConstraints();
					this.onDateChange();
					this.updateDateDisplay();
				});
				
				// Prevent manual keyboard entry
				this.dateInput.addEventListener('keydown', (e) => {
					if (e.key !== 'Tab') {
						e.preventDefault();
					}
				});
			}
		}

		/**
		 * ✅ FIXED: Get current date/time in GMT+8 (store timezone)
		 */
		getCurrentTime() {
			const now = new Date();
			// Convert local time to UTC, then add GMT+8 offset
			const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
			return new Date(utcTime + (this.timezoneOffset * 60000));
		}

		/**
		 * ✅ FIXED: Get today's date at midnight in GMT+8
		 */
		getTodayMidnight() {
			const now = this.getCurrentTime();
			return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
		}

		/**
		 * Check if current time is past the cutoff (6:30 PM GMT+8)
		 */
		isPastCutoff() {
			const now = this.getCurrentTime();
			return (now.getHours() > this.cutoffHour) ||
				   (now.getHours() === this.cutoffHour && now.getMinutes() >= this.cutoffMinute);
		}

		/**
		 * ✅ FIXED: Get the base date - NEVER returns yesterday
		 * If past 6:30 PM GMT+8, base date is tomorrow; otherwise today
		 */
		getBaseDate() {
			const todayMidnight = this.getTodayMidnight();
			
			// If past cutoff, shift to tomorrow
			if (this.isPastCutoff()) {
				const tomorrow = new Date(todayMidnight);
				tomorrow.setDate(tomorrow.getDate() + 1);
				return tomorrow;
			}
			
			return todayMidnight;
		}

		/**
		 * Get the first available date (base + leadDays)
		 */
		getFirstAvailableDate() {
			const baseDate = this.getBaseDate();
			const firstDate = new Date(baseDate);
			firstDate.setDate(baseDate.getDate() + this.leadDays);
			return firstDate;
		}

		/**
		 * First day of the booking window (lead rules + never before store today).
		 * May still be a closed date — use getFirstSelectableDate() for pickers.
		 */
		getCalendarFirstDate() {
			let firstDate = this.getFirstAvailableDate();
			const storeToday = this.getTodayMidnight();
			if (firstDate < storeToday) {
				firstDate = new Date(storeToday);
			}
			return firstDate;
		}

		/**
		 * Last selectable day: (availableDays) calendar days starting from the first *open* day,
		 * so closed days at the start of the window do not shrink the range to nothing.
		 */
		getLastAvailableDate() {
			const firstSelectable = this.getFirstSelectableDate();
			let lastDate = new Date(firstSelectable);
			lastDate.setDate(firstSelectable.getDate() + this.availableDays - 1);

			if (this.maxDate) {
				const parts = this.maxDate.split('-');
				const maxDateObj = new Date(
					parseInt(parts[0], 10),
					parseInt(parts[1], 10) - 1,
					parseInt(parts[2], 10),
					0, 0, 0, 0
				);
				if (maxDateObj < lastDate) {
					lastDate = maxDateObj;
				}
			}

			if (lastDate < firstSelectable) {
				lastDate = new Date(firstSelectable);
			}

			return lastDate;
		}

		parseTimeslotConfig() {
			const configStr = this.dataset.timeslotConfig;
			if (!configStr) return {};
			try {
				return JSON.parse(configStr);
			} catch (e) {
				console.warn('Invalid timeslot config:', e);
				return {};
			}
		}

		getHoursForDate(dateValue) {
			if (this.dateOverrides && this.dateOverrides[dateValue]) {
				const override = this.dateOverrides[dateValue];
				return {
					startHour: override.startHour ?? this.startHour,
					endHour: override.endHour ?? this.endHour
				};
			}
			return { startHour: this.startHour, endHour: this.endHour };
		}

		isDateClosed(dateValue) {
			return this.closedDates && this.closedDates.includes(dateValue);
		}

		/**
		 * First calendar day on or after startDate that is not in closedDates (within last available).
		 */
		getNextOpenDateFrom(startDate) {
			const lastDate = this.getLastAvailableDate();
			let d = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0);
			const end = new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate(), 0, 0, 0, 0);
			while (d <= end) {
				const str = this.formatDateValue(d);
				if (!this.isDateClosed(str)) {
					return new Date(d);
				}
				d.setDate(d.getDate() + 1);
			}
			return null;
		}

		/**
		 * First open day on or after calendar window start. Does not call getLastAvailableDate()
		 * (that method depends on this, and the old chain broke when closed days filled the whole short window).
		 */
		getFirstSelectableDate() {
			let d = this.getCalendarFirstDate();
			const limit = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
			limit.setDate(limit.getDate() + 366);
			while (d <= limit) {
				const str = this.formatDateValue(d);
				if (!this.isDateClosed(str)) {
					return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
				}
				d.setDate(d.getDate() + 1);
			}
			return this.getCalendarFirstDate();
		}

		formatDateValue(date) {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const day = String(date.getDate()).padStart(2, '0');
			return `${year}-${month}-${day}`;
		}

		formatDateDisplay(dateValue) {
			if (!dateValue) return '';
			const parts = dateValue.split('-');
			if (parts.length !== 3) return dateValue;
			const month = parseInt(parts[1]) - 1;
			const day = parseInt(parts[2]);
			const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
			return `${monthNames[month]} ${String(day).padStart(2, '0')}`;
		}

		setupCalendarPicker() {
			if (!this.dateInput) return;
			
			const field = this.dateInput.closest('.datetime-slot-picker__field');
			if (!field) return;
			
			// Disable native calendar
			this.dateInput.setAttribute('readonly', 'readonly');
			this.dateInput.setAttribute('tabindex', '-1');
			this.dateInput.style.cursor = 'pointer';
			this.dateInput.style.color = 'transparent';
			this.dateInput.style.caretColor = 'transparent';
			
			const preventNativeCalendar = (e) => {
				e.preventDefault();
				e.stopPropagation();
				this.dateInput.blur();
				setTimeout(() => this.toggleCalendar(), 0);
				return false;
			};
			
			this.dateInput.addEventListener('click', (e) => {
				e.preventDefault();
				if (!this.calendarOpen) this.openCalendar();
				e.calendarHandled = true;
			}, { passive: false });
			
			this.dateInput.addEventListener('mousedown', preventNativeCalendar, { passive: false, capture: true });
			this.dateInput.addEventListener('touchstart', preventNativeCalendar, { passive: false, capture: true });
			this.dateInput.addEventListener('focus', preventNativeCalendar, { passive: false, capture: true });
			
			// Create calendar container
			this.calendarContainer = document.createElement('div');
			this.calendarContainer.className = 'custom-calendar-picker';
			this.calendarContainer.style.display = 'none';
			if (getComputedStyle(field).position === 'static') {
				field.style.position = 'relative';
			}
			field.appendChild(this.calendarContainer);
			this.calendarField = field;
			
			// Close on outside click
			this.outsideClickHandler = (e) => {
				if (this.calendarOpen) {
					const isDateInput = this.dateInput && (e.target === this.dateInput || this.dateInput.contains(e.target));
					const isCalendar = this.calendarContainer && (e.target === this.calendarContainer || this.calendarContainer.contains(e.target));
					const isField = field && (e.target === field || field.contains(e.target));
					if (!isDateInput && !isCalendar && !isField) {
						this.closeCalendar();
					}
				}
			};
			document.addEventListener('click', this.outsideClickHandler, false);
			
			this.updateCalendarPosition = () => {
				if (this.calendarOpen && window.innerWidth > 767 && this.updateDesktopPosition) {
					this.updateDesktopPosition();
				}
			};
			window.addEventListener('scroll', this.updateCalendarPosition, true);
			window.addEventListener('resize', this.updateCalendarPosition);
			
			this.buildCalendar();
		}

		buildCalendar() {
			if (!this.calendarContainer) return;
			
			let firstSelectable = this.getFirstSelectableDate();
			const lastDate = this.getLastAvailableDate();
			
			if (firstSelectable > lastDate) firstSelectable = new Date(lastDate);
			
			const currentValue = this.dateInput.value || this.formatDateValue(firstSelectable);
			let selectedDate = currentValue ? new Date(currentValue + 'T00:00:00') : new Date(firstSelectable);
			if (this.isDateClosed(this.formatDateValue(selectedDate))) {
				selectedDate = new Date(firstSelectable);
			}
			
			const header = document.createElement('div');
			header.className = 'calendar-header';
			header.innerHTML = `
				<button type="button" class="calendar-nav calendar-prev" aria-label="Previous">‹</button>
				<span class="calendar-month-year"></span>
				<button type="button" class="calendar-nav calendar-next" aria-label="Next">›</button>
			`;
			
			const grid = document.createElement('div');
			grid.className = 'calendar-grid';
			
			this.calendarContainer.innerHTML = '';
			this.calendarContainer.appendChild(header);
			this.calendarContainer.appendChild(grid);
			
			this.currentMonth = new Date(selectedDate);
			this.renderCalendar();
			
			header.querySelector('.calendar-prev').addEventListener('click', () => {
				this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
				this.renderCalendar();
			});
			
			header.querySelector('.calendar-next').addEventListener('click', () => {
				this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
				this.renderCalendar();
			});
		}

		renderCalendar() {
			if (!this.calendarContainer) return;
			
			const grid = this.calendarContainer.querySelector('.calendar-grid');
			const monthYear = this.calendarContainer.querySelector('.calendar-month-year');
			
			const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
				'July', 'August', 'September', 'October', 'November', 'December'];
			monthYear.textContent = `${monthNames[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;
			
			grid.innerHTML = '';
			
			const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
			dayHeaders.forEach(day => {
				const dayHeader = document.createElement('div');
				dayHeader.className = 'calendar-day-header';
				dayHeader.textContent = day;
				grid.appendChild(dayHeader);
			});
			
			const firstDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), 1);
			const lastDay = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 0);
			const daysInMonth = lastDay.getDate();
			const startingDayOfWeek = firstDay.getDay();
			
			const firstDate = this.getFirstSelectableDate();
			const lastDate = this.getLastAvailableDate();
			
			for (let i = 0; i < startingDayOfWeek; i++) {
				const emptyCell = document.createElement('div');
				emptyCell.className = 'calendar-day calendar-day-empty';
				grid.appendChild(emptyCell);
			}
			
			const currentValue = this.dateInput.value;
			for (let day = 1; day <= daysInMonth; day++) {
				const date = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth(), day);
				const dateValue = this.formatDateValue(date);
				const isAvailable = date >= firstDate && date <= lastDate && !this.isDateClosed(dateValue);
				const isSelected = currentValue === dateValue;
				
				const dayCell = document.createElement('button');
				dayCell.type = 'button';
				dayCell.className = 'calendar-day';
				dayCell.textContent = day;
				
				if (!isAvailable) {
					dayCell.classList.add('calendar-day-disabled');
					dayCell.disabled = true;
				} else {
					dayCell.classList.add('calendar-day-available');
					if (isSelected) dayCell.classList.add('calendar-day-selected');
					
					dayCell.addEventListener('click', () => {
						this.dateInput.value = dateValue;
						this.updateDateDisplay();
						this.onDateChange();
						this.closeCalendar();
					});
				}
				
				grid.appendChild(dayCell);
			}
			
			const prevBtn = this.calendarContainer.querySelector('.calendar-prev');
			const nextBtn = this.calendarContainer.querySelector('.calendar-next');
			
			const minMonth = new Date(firstDate.getFullYear(), firstDate.getMonth());
			const maxMonth = new Date(lastDate.getFullYear(), lastDate.getMonth());
			const currentMonthStart = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth());
			
			prevBtn.disabled = currentMonthStart <= minMonth;
			nextBtn.disabled = currentMonthStart >= maxMonth;
		}

		toggleCalendar() {
			this.calendarOpen ? this.closeCalendar() : this.openCalendar();
		}

		openCalendar() {
			if (!this.calendarContainer || !this.calendarField) return;
			this.calendarOpen = true;
			this.calendarField.classList.add('calendar-open');
			
			const isMobile = window.innerWidth <= 767;
			
			if (isMobile) {
				if (this.calendarContainer.parentNode !== document.body) {
					document.body.appendChild(this.calendarContainer);
				}
				this.calendarContainer.style.position = 'fixed';
				this.calendarContainer.style.top = '50%';
				this.calendarContainer.style.left = '50%';
				this.calendarContainer.style.transform = 'translate(-50%, -50%)';
				this.calendarContainer.style.width = 'calc(100vw - 2rem)';
				this.calendarContainer.style.maxWidth = '320px';
				this.calendarContainer.style.zIndex = '998';
			} else {
				if (this.calendarContainer.parentNode !== document.body) {
					document.body.appendChild(this.calendarContainer);
				}
				const updateDesktopPosition = () => {
					const inputRect = this.dateInput.getBoundingClientRect();
					const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
					const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
					
					this.calendarContainer.style.position = 'absolute';
					this.calendarContainer.style.top = `${inputRect.bottom + scrollTop + 8}px`;
					this.calendarContainer.style.left = `${inputRect.left + scrollLeft}px`;
					this.calendarContainer.style.width = 'auto';
					this.calendarContainer.style.minWidth = '280px';
					this.calendarContainer.style.maxWidth = '320px';
					this.calendarContainer.style.transform = 'none';
				};
				updateDesktopPosition();
				this.updateDesktopPosition = updateDesktopPosition;
				this.calendarContainer.style.zIndex = '99';
			}
			
			this.calendarContainer.style.display = 'block';
			this.calendarContainer.style.background = 'var(--color-background-main, #ffffff)';
			
			if (this.dateInput.value) {
				this.currentMonth = new Date(this.dateInput.value + 'T00:00:00');
			}
			this.renderCalendar();
		}

		closeCalendar() {
			if (!this.calendarContainer) return;
			this.calendarOpen = false;
			this.calendarContainer.style.display = 'none';
			if (this.calendarField) this.calendarField.classList.remove('calendar-open');
			if (this.updateCalendarPosition) {
				window.removeEventListener('scroll', this.updateCalendarPosition, true);
				window.removeEventListener('resize', this.updateCalendarPosition);
			}
			this.updateDesktopPosition = null;
		}

		/**
		 * ✅ FIXED: Validate date - NEVER allows yesterday or past dates
		 */
		isDateValid(dateStr) {
			if (!dateStr) return false;
			
			const storeToday = this.getTodayMidnight();
			const todayStr = this.formatDateValue(storeToday);
			
			// ✅ Critical: Never allow dates before today in GMT+8
			if (dateStr < todayStr) return false;
			
			if (this.isDateClosed(dateStr)) return false;
			return dateStr >= this.minDateStr && dateStr <= this.maxDateStr;
		}

		/**
		 * ✅ FIXED: Enforce date constraints - prevents yesterday
		 */
		enforceDateConstraints() {
			if (!this.dateInput) return;

			const selectedValue = this.dateInput.value;
			const storeToday = this.getTodayMidnight();
			const todayStr = this.formatDateValue(storeToday);

			// Ensure min is at least today (never yesterday)
			const effectiveMin = this.minDateStr < todayStr ? todayStr : this.minDateStr;
			this.dateInput.setAttribute('min', effectiveMin);
			this.dateInput.setAttribute('max', this.maxDateStr);

			// Reset if invalid, before today, or closed date → snap to next open day
			const needsReset = !selectedValue || selectedValue < effectiveMin || selectedValue < todayStr ||
				this.isDateClosed(selectedValue);

			if (needsReset) {
				const base = selectedValue && selectedValue >= effectiveMin && selectedValue >= todayStr
					? new Date(selectedValue + 'T00:00:00')
					: new Date((this.minDateStr < todayStr ? todayStr : this.minDateStr) + 'T00:00:00');
				let next = this.getNextOpenDateFrom(base);
				if (!next) {
					next = this.getFirstSelectableDate();
				}
				this.dateInput.value = this.formatDateValue(next);
				this.updateDateDisplay();
				this.populateTimeOptions();
			}
		}

		/**
		 * ✅ FIXED: Setup date constraints - ensures yesterday is NEVER shown
		 */
		setupDateConstraints() {
			if (!this.dateInput) return;

			const firstSelectable = this.getFirstSelectableDate();
			const lastDate = this.getLastAvailableDate();

			const minDateStr = this.formatDateValue(firstSelectable);
			const maxDateStr = this.formatDateValue(lastDate);

			this.minDateStr = minDateStr;
			this.maxDateStr = maxDateStr;

			const initialValueStr = minDateStr;

			// min = first open day so the default is never a closed date inside a short window
			this.dateInput.setAttribute('min', minDateStr);
			this.dateInput.setAttribute('max', maxDateStr);
			this.dateInput.setAttribute('step', '1');
			this.dateInput.value = initialValueStr;
			this.dateInput.setCustomValidity('');

			// Re-apply constraints on focus (before calendar opens)
			this.dateInput.addEventListener('focus', () => {
				this.enforceDateConstraints();
			});

			// Re-apply constraints periodically
			this.constraintInterval = setInterval(() => {
				this.enforceDateConstraints();
			}, 1000);

			setTimeout(() => {
				this.updateDateDisplay();
				this.populateTimeOptions();
			}, 0);
		}

		generateTimeSlots(forDate) {
			const slots = [];
			const dateValue = forDate || (this.dateInput ? this.dateInput.value : '');
			const { startHour, endHour } = this.getHoursForDate(dateValue);
			
			for (let hour = startHour; hour < endHour; hour++) {
				for (let minute = 0; minute < 60; minute += this.slotDuration) {
					const endHourCalc = minute + this.slotDuration >= 60 ? hour + 1 : hour;
					const endMinute = (minute + this.slotDuration) % 60;
					
					if (endHourCalc > endHour || (endHourCalc === endHour && endMinute > 0)) continue;
					
					slots.push({
						display: `${this.formatTime12Hour(hour, minute)} - ${this.formatTime12Hour(endHourCalc, endMinute)}`,
						value: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
						hour: hour,
						minute: minute
					});
				}
			}
			
			return slots;
		}

		formatTime12Hour(hour, minute) {
			const period = hour >= 12 ? 'PM' : 'AM';
			const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
			return `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
		}

		/**
		 * Check if time slot should be disabled (uses GMT+8)
		 */
		shouldDisableSlot(slot, selectedDateValue) {
			if (!selectedDateValue) return false;

			const now = this.getCurrentTime();
			const todayMidnight = this.getTodayMidnight();

			const selectedParts = selectedDateValue.split('-');
			const selectedDate = new Date(
				parseInt(selectedParts[0]),
				parseInt(selectedParts[1]) - 1,
				parseInt(selectedParts[2]),
				0, 0, 0, 0
			);

			// Only restrict times if selected date is TODAY (GMT+8)
			if (selectedDate.getTime() !== todayMidnight.getTime()) {
				return false;
			}

			const minAllowedMinutes = (now.getHours() * 60) + now.getMinutes() + this.bufferMinutes;
			const slotMinutes = (slot.hour * 60) + slot.minute;

			return slotMinutes < minAllowedMinutes;
		}

		populateTimeOptions() {
			if (!this.timeSelect) return;
			
			const currentValue = this.timeSelect.value && this.timeSelect.value !== '' ? this.timeSelect.value : null;
			this.timeSelect.innerHTML = '';
			
			const selectedDateValue = this.dateInput ? this.dateInput.value : '';
			const allSlots = this.generateTimeSlots();
			
			let currentValueStillAvailable = false;
			let defaultTimeAvailable = false;
			
			allSlots.forEach((slot) => {
				if (this.shouldDisableSlot(slot, selectedDateValue)) return;
				
				const option = document.createElement('option');
				option.value = slot.value;
				option.textContent = slot.display;
				
				if (slot.value === '10:00') defaultTimeAvailable = true;
				if (currentValue && slot.value === currentValue) currentValueStillAvailable = true;
				
				this.timeSelect.appendChild(option);
			});
			
			if (currentValue && currentValueStillAvailable) {
				this.timeSelect.value = currentValue;
			} else if (defaultTimeAvailable) {
				this.timeSelect.value = '10:00';
			} else if (this.timeSelect.options.length > 0) {
				this.timeSelect.selectedIndex = 0;
			}

			// No slots (e.g. closed day still selected): leave empty so validate() + checkout block
			if (this.timeSelect.options.length === 0) {
				this.timeSelect.value = '';
			}
		}

		setupDateDisplay() {
			if (!this.dateInput) return;
			
			const field = this.dateInput.closest('.datetime-slot-picker__field');
			if (field) {
				let displayElement = field.querySelector('.date-display-overlay');
				if (!displayElement) {
					displayElement = document.createElement('span');
					displayElement.className = 'date-display-overlay';
					displayElement.setAttribute('aria-hidden', 'true');
					field.style.position = 'relative';
					this.dateInput.parentNode.insertBefore(displayElement, this.dateInput);
				}
				this.displayElement = displayElement;
				this.dateInput.style.color = 'transparent';
				this.setupArrowIcon(this.dateInput);
				this.updateDateDisplay();
			}
		}

		ensureArrowIcons() {
			if (this.dateInput) this.setupArrowIcon(this.dateInput);
			if (this.timeSelect) this.setupArrowIcon(this.timeSelect);
		}

		setupArrowIcon(inputElement) {
			if (!inputElement) return;
			
			const field = inputElement.closest('.datetime-slot-picker__field');
			if (!field) return;
			
			if (getComputedStyle(field).position === 'static') {
				field.style.position = 'relative';
			}
			
			const inputId = inputElement.id || inputElement.getAttribute('name') || '';
			const arrowSelector = inputId ? `.dropdown-arrow-icon[data-for="${inputId}"]` : '.dropdown-arrow-icon';
			let arrowElement = field.querySelector(arrowSelector);
			
			if (!arrowElement) {
				arrowElement = document.createElement('span');
				arrowElement.className = 'dropdown-arrow-icon';
				arrowElement.setAttribute('aria-hidden', 'true');
				if (inputId) arrowElement.setAttribute('data-for', inputId);
				field.appendChild(arrowElement);
			}
		}

		updateDateDisplay() {
			const dateValue = this.dateInput ? this.dateInput.value : '';
			
			if (dateValue && this.displayElement) {
				const formattedDate = this.formatDateDisplay(dateValue);
				this.displayElement.textContent = formattedDate;
				if (this.dateInput) this.dateInput.setAttribute('title', formattedDate);
			} else if (this.displayElement) {
				this.displayElement.textContent = '';
			}
		}

		onDateChange() {
			this.populateTimeOptions();
			this.updateDateDisplay();
		}

		getValue() {
			const date = this.dateInput ? this.dateInput.value : '';
			const time = this.timeSelect?.value || '';
			const timeDisplay = this.timeSelect?.selectedOptions[0]?.text || '';
			
			if (date && time) {
				return { date, time, timeDisplay, combined: `${date} ${time}` };
			}
			return null;
		}

		validate() {
			const dateValue = this.dateInput ? this.dateInput.value : '';
			if (!dateValue) return { valid: false, message: 'Please select a pickup date' };
			if (this.isDateClosed(dateValue)) {
				return { valid: false, message: 'This date is not available for pickup. Please choose another date.' };
			}

			if (!this.timeSelect) {
				return { valid: false, message: 'Please select a time slot' };
			}
			if (this.timeSelect.options.length === 0) {
				return { valid: false, message: 'No time slots for this date. Please choose another date.' };
			}
			const timeVal = this.timeSelect.value;
			if (!timeVal || String(timeVal).trim() === '') {
				return { valid: false, message: 'Please select a time slot' };
			}

			const selectedOption = this.timeSelect.selectedOptions[0];
			if (selectedOption?.disabled) return { valid: false, message: 'Please select an available time slot' };

			const selectedParts = dateValue.split('-');
			const selectedDate = new Date(parseInt(selectedParts[0]), parseInt(selectedParts[1]) - 1, parseInt(selectedParts[2]), 0, 0, 0, 0);

			const firstDate = this.getFirstSelectableDate();
			const lastDate = this.getLastAvailableDate();

			if (selectedDate < firstDate || selectedDate > lastDate) {
				return { valid: false, message: 'Please select a valid date within the allowed range' };
			}

			return { valid: true };
		}

		disconnectedCallback() {
			if (this.constraintInterval) {
				clearInterval(this.constraintInterval);
				this.constraintInterval = null;
			}
			if (this.outsideClickHandler) {
				document.removeEventListener('click', this.outsideClickHandler);
			}
			if (this.calendarContainer && this.calendarContainer.parentNode) {
				this.calendarContainer.parentNode.removeChild(this.calendarContainer);
			}
		}

	}

	if (typeof customElements.get('datetime-slot-picker') == 'undefined') {
		customElements.define('datetime-slot-picker', DateTimeSlotPicker);
	}

}